import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn/cn.util';

import {
  STRUCTURED_READ_ONLY_CLASSES,
  STRUCTURED_SHELL_CLASSES,
  STRUCTURED_TOOLBAR_CLASSES,
} from '../structured.tokens';

import { type StructuredShellProps } from './StructuredShell.types';

/**
 * The frame both structured editors sit in: the column, the toolbar, the
 * read-only notice, and — the part that has to be shared — **exactly one**
 * `role="status"` region.
 *
 * One live region per control, never two. Two regions in one control means two
 * things announcing at once and a screen reader picking whichever it feels like;
 * the selection family carries the same rule in `SelectionPanel` for the same
 * reason. Everything either editor wants to say goes through the announcer hook
 * and lands here.
 *
 * Presentation only (U5): every decision arrives as a prop.
 */
export function StructuredShell({
  role,
  id,
  className,
  label,
  'aria-describedby': describedBy,
  isReadOnly = false,
  toolbar,
  announcement,
  children,
  footer,
}: StructuredShellProps) {
  const { t } = useTranslation('uiKit');

  return (
    <div
      id={id}
      role={role}
      // A `group` needs naming; a body that names itself (a textarea with its
      // own label) must not be named twice, so the attribute follows the role.
      aria-label={role === undefined ? undefined : label}
      aria-describedby={role === undefined ? undefined : describedBy}
      className={cn(STRUCTURED_SHELL_CLASSES, className)}
    >
      {toolbar === undefined ? null : <div className={STRUCTURED_TOOLBAR_CLASSES}>{toolbar}</div>}

      {isReadOnly ? (
        <p className={STRUCTURED_READ_ONLY_CLASSES}>
          <Lock aria-hidden className="h-4 w-4 shrink-0" />
          {t('structured.readOnly')}
        </p>
      ) : null}

      {children}

      {footer}

      {/*
        Always mounted, never conditionally rendered: a live region added to the
        DOM at the same moment it gains text is frequently not announced at all,
        because the observer has nothing to observe a change ON.
      */}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </div>
  );
}
